/**
 * /address — 收货地址列表 + 新增/编辑 Modal
 *
 * 设计稿: _design_reference/mini-address.html
 *
 * 功能:
 * 1. 列表展示所有地址(默认地址渐变边框卡片)
 * 2. 地址计数副标题
 * 3. 地址标签(设计稿中无后端字段,视觉占位用 isDefault 区分,其余地址不显标签)
 * 4. 电话格式化: +7 999 123 45 67
 * 5. 卡片底部操作行: 设默认 / 编辑 / 复制 / 删除
 * 6. 内联虚线新增卡片 + "还可添加 N 个" 文案
 * 7. AddressFormModal (新增/编辑)
 */

import {
  useAddAddress,
  useAddresses,
  useDeleteAddresses,
  useDivisionCities,
  useDivisionStates,
  useEditAddress,
  useSetDefaultAddress,
  type AddressAddInput,
  type AddressVo,
} from "@kupikupi/api";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth, useAuthToken } from "../lib/auth";
import {
  formatRuMobileDisplay,
  isValidRuPostalCode,
  normalizeRuMobileDigits,
  RU_COUNTRY_CODE,
  RU_COUNTRY_TEXT,
  RU_REGION_CODE,
  toRuMobileE164,
} from "../lib/address-validation";

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_ADDRESSES = 10;

// ─── Form types ───────────────────────────────────────────────────────────────
interface FormState {
  recipientName: string;
  mobile: string;
  provinceCode: string;
  provinceText: string;
  cityCode: string;
  cityText: string;
  addressDetail: string;
  postalCode: string;
  doorNumber: string;
  isDefault: boolean;
}

type FieldErrors = Partial<Record<"mobile" | "province" | "city" | "postal", string | undefined>>;

function emptyForm(): FormState {
  return {
    recipientName: "",
    mobile: "",
    provinceCode: "",
    provinceText: "",
    cityCode: "",
    cityText: "",
    addressDetail: "",
    postalCode: "",
    doorNumber: "",
    isDefault: false,
  };
}

function fromAddress(a: AddressVo): FormState {
  return {
    recipientName: a.recipientName || "",
    mobile: normalizeRuMobileDigits(a.mobile || "") ?? a.mobile ?? "",
    provinceCode: a.provinceCode || "",
    provinceText: a.province || "",
    cityCode: a.cityCode || "",
    cityText: a.city || "",
    addressDetail: a.detail || "",
    postalCode: a.postalCode || "",
    doorNumber: a.doorNumber || "",
    isDefault: a.isDefault,
  };
}

function toInput(state: FormState, mobileE164: string): AddressAddInput {
  const out: AddressAddInput = {
    recipientName: state.recipientName.trim(),
    regionCode: RU_REGION_CODE,
    mobile: mobileE164,
    countryCode: RU_COUNTRY_CODE,
    countryText: RU_COUNTRY_TEXT,
    addressDetail: state.addressDetail.trim(),
    isDefault: state.isDefault,
  };
  if (state.provinceCode) out.provinceCode = state.provinceCode;
  if (state.provinceText) out.provinceText = state.provinceText;
  if (state.cityCode) out.cityCode = state.cityCode;
  if (state.cityText) out.cityText = state.cityText;
  const postalCode = state.postalCode.trim();
  if (postalCode) out.postalCode = postalCode;
  const doorNumber = state.doorNumber.trim();
  if (doorNumber) out.doorNumber = doorNumber;
  return out;
}

// ─── AddressFormModal ─────────────────────────────────────────────────────────
interface FormModalProps {
  authToken: string | null;
  initial?: AddressVo | null;
  onClose: () => void;
}

function AddressFormModal({ authToken, initial, onClose }: FormModalProps) {
  const { t } = useTranslation("address");
  const isEdit = !!initial;
  const [state, setState] = useState<FormState>(() =>
    initial ? fromAddress(initial) : emptyForm(),
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const addMut = useAddAddress(authToken);
  const editMut = useEditAddress(authToken);
  const busy = addMut.isPending || editMut.isPending;

  const states = useDivisionStates(RU_COUNTRY_CODE);
  const cities = useDivisionCities(state.provinceCode || null);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setState((s) => ({ ...s, [k]: v }));

  const onProvinceChange = (id: string) => {
    const opt = states.data?.find((s) => s.id === id);
    setState((s) => ({
      ...s,
      provinceCode: id,
      provinceText: opt?.name ?? "",
      cityCode: "",
      cityText: "",
    }));
    setFieldErrors((e) => ({ ...e, province: undefined, city: undefined }));
  };

  const onCityChange = (id: string) => {
    const opt = cities.data?.find((c) => c.id === id);
    setState((s) => ({
      ...s,
      cityCode: id,
      cityText: opt?.name ?? "",
      postalCode: s.postalCode.trim() === "" && opt?.postalCode ? opt.postalCode : s.postalCode,
    }));
    setFieldErrors((e) => ({ ...e, city: undefined }));
  };

  const baseValid =
    state.recipientName.trim().length > 0 &&
    state.mobile.trim().length > 0 &&
    state.addressDetail.trim().length > 0;

  const validate = (): { ok: boolean; mobileE164: string } => {
    const errs: FieldErrors = {};
    const mobileE164 = toRuMobileE164(state.mobile);
    if (!mobileE164) errs.mobile = t("form.err.mobile");
    if (!state.provinceCode) errs.province = t("form.err.province");
    if (!state.cityCode) errs.city = t("form.err.city");
    if (state.postalCode.trim() && !isValidRuPostalCode(state.postalCode)) {
      errs.postal = t("form.err.postal");
    }
    setFieldErrors(errs);
    return { ok: Object.keys(errs).length === 0, mobileE164: mobileE164 ?? "" };
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!baseValid || busy) return;
    setError(null);
    const { ok, mobileE164 } = validate();
    if (!ok) return;
    try {
      if (isEdit && initial) {
        await editMut.mutateAsync({ ...toInput(state, mobileE164), id: initial.id });
      } else {
        await addMut.mutateAsync(toInput(state, mobileE164));
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("form.submit_failed"));
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(11,12,15,0.6)",
        zIndex: 200,
        display: "flex",
        alignItems: "flex-end",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: "100%",
          background: "var(--color-surface,#fff)",
          borderRadius: "16px 16px 0 0",
          padding: "20px 16px 32px",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <strong style={{ fontSize: 15, fontWeight: 800 }}>
            {isEdit ? t("form.title_edit") : t("form.title_add")}
          </strong>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={t("form.close")}
            style={{
              background: "transparent",
              border: 0,
              fontSize: 20,
              color: "var(--color-text-muted)",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>

        {/* Recipient */}
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="mini-addr-name"
            style={{ fontSize: 11, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}
          >
            {t("form.recipient_l")} *
          </label>
          <input
            id="mini-addr-name"
            value={state.recipientName}
            onChange={(e) => update("recipientName", e.target.value)}
            maxLength={32}
            disabled={busy}
            style={{
              width: "100%",
              padding: "9px 12px",
              border: "1.5px solid var(--color-border)",
              borderRadius: 10,
              fontSize: 13,
              boxSizing: "border-box",
              background: "var(--color-surface,#fff)",
            }}
          />
        </div>

        {/* Phone */}
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="mini-addr-mobile"
            style={{ fontSize: 11, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}
          >
            {t("form.region_l")} *
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={RU_REGION_CODE}
              readOnly
              disabled={busy}
              style={{
                width: 52,
                padding: "9px 8px",
                border: "1.5px solid var(--color-border)",
                borderRadius: 10,
                fontSize: 13,
                textAlign: "center",
                background: "var(--color-surface-muted,#f5f5f5)",
                boxSizing: "border-box",
              }}
            />
            <input
              id="mini-addr-mobile"
              value={state.mobile}
              onChange={(e) => {
                update("mobile", e.target.value);
                if (fieldErrors.mobile) setFieldErrors((er) => ({ ...er, mobile: undefined }));
              }}
              placeholder={t("form.mobile_placeholder")}
              inputMode="tel"
              disabled={busy}
              style={{
                flex: 1,
                padding: "9px 12px",
                border: `1.5px solid ${fieldErrors.mobile ? "var(--color-danger,#d4543b)" : "var(--color-border)"}`,
                borderRadius: 10,
                fontSize: 13,
                background: "var(--color-surface,#fff)",
                boxSizing: "border-box",
              }}
            />
          </div>
          {fieldErrors.mobile && (
            <p style={{ fontSize: 11, color: "var(--color-danger,#d4543b)", margin: "4px 0 0" }}>
              {fieldErrors.mobile}
            </p>
          )}
        </div>

        {/* Province */}
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="mini-addr-province"
            style={{ fontSize: 11, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}
          >
            {t("form.province_l")} *
          </label>
          <select
            id="mini-addr-province"
            value={state.provinceCode}
            onChange={(e) => onProvinceChange(e.target.value)}
            disabled={busy || states.isLoading}
            style={{
              width: "100%",
              padding: "9px 12px",
              border: `1.5px solid ${fieldErrors.province ? "var(--color-danger,#d4543b)" : "var(--color-border)"}`,
              borderRadius: 10,
              fontSize: 13,
              background: "var(--color-surface,#fff)",
              boxSizing: "border-box",
              appearance: "auto",
            }}
          >
            <option value="">
              {states.isLoading ? t("form.loading") : t("form.province_placeholder")}
            </option>
            {state.provinceCode && !states.data?.some((s) => s.id === state.provinceCode) && (
              <option value={state.provinceCode}>
                {state.provinceText || state.provinceCode}
              </option>
            )}
            {(states.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {fieldErrors.province && (
            <p style={{ fontSize: 11, color: "var(--color-danger,#d4543b)", margin: "4px 0 0" }}>
              {fieldErrors.province}
            </p>
          )}
        </div>

        {/* City */}
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="mini-addr-city"
            style={{ fontSize: 11, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}
          >
            {t("form.city_l")} *
          </label>
          <select
            id="mini-addr-city"
            value={state.cityCode}
            onChange={(e) => onCityChange(e.target.value)}
            disabled={busy || !state.provinceCode || cities.isLoading}
            style={{
              width: "100%",
              padding: "9px 12px",
              border: `1.5px solid ${fieldErrors.city ? "var(--color-danger,#d4543b)" : "var(--color-border)"}`,
              borderRadius: 10,
              fontSize: 13,
              background: "var(--color-surface,#fff)",
              boxSizing: "border-box",
              appearance: "auto",
            }}
          >
            <option value="">
              {!state.provinceCode
                ? t("form.city_needs_province")
                : cities.isLoading
                  ? t("form.loading")
                  : t("form.city_placeholder")}
            </option>
            {state.cityCode && !cities.data?.some((c) => c.id === state.cityCode) && (
              <option value={state.cityCode}>{state.cityText || state.cityCode}</option>
            )}
            {(cities.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {fieldErrors.city && (
            <p style={{ fontSize: 11, color: "var(--color-danger,#d4543b)", margin: "4px 0 0" }}>
              {fieldErrors.city}
            </p>
          )}
        </div>

        {/* Detail */}
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="mini-addr-detail"
            style={{ fontSize: 11, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}
          >
            {t("form.detail_l")} *
          </label>
          <textarea
            id="mini-addr-detail"
            rows={3}
            value={state.addressDetail}
            onChange={(e) => update("addressDetail", e.target.value)}
            placeholder={t("form.detail_placeholder")}
            disabled={busy}
            style={{
              width: "100%",
              padding: "9px 12px",
              border: "1.5px solid var(--color-border)",
              borderRadius: 10,
              fontSize: 13,
              background: "var(--color-surface,#fff)",
              boxSizing: "border-box",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
        </div>

        {/* Postal + Door row */}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label
              htmlFor="mini-addr-postal"
              style={{ fontSize: 11, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}
            >
              {t("form.postal_l")}
            </label>
            <input
              id="mini-addr-postal"
              value={state.postalCode}
              onChange={(e) => {
                update("postalCode", e.target.value);
                if (fieldErrors.postal) setFieldErrors((er) => ({ ...er, postal: undefined }));
              }}
              inputMode="numeric"
              maxLength={6}
              placeholder={t("form.postal_placeholder")}
              disabled={busy}
              style={{
                width: "100%",
                padding: "9px 12px",
                border: `1.5px solid ${fieldErrors.postal ? "var(--color-danger,#d4543b)" : "var(--color-border)"}`,
                borderRadius: 10,
                fontSize: 13,
                background: "var(--color-surface,#fff)",
                boxSizing: "border-box",
              }}
            />
            {fieldErrors.postal && (
              <p style={{ fontSize: 11, color: "var(--color-danger,#d4543b)", margin: "4px 0 0" }}>
                {fieldErrors.postal}
              </p>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <label
              htmlFor="mini-addr-door"
              style={{ fontSize: 11, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}
            >
              {t("form.door_l")}
            </label>
            <input
              id="mini-addr-door"
              value={state.doorNumber}
              onChange={(e) => update("doorNumber", e.target.value)}
              disabled={busy}
              style={{
                width: "100%",
                padding: "9px 12px",
                border: "1.5px solid var(--color-border)",
                borderRadius: 10,
                fontSize: 13,
                background: "var(--color-surface,#fff)",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

        {/* Default toggle */}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 18,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={state.isDefault}
            onChange={(e) => update("isDefault", e.target.checked)}
            disabled={busy}
            style={{ width: 16, height: 16, accentColor: "var(--color-primary-fill)" }}
          />
          <span style={{ fontSize: 13 }}>{t("form.default_l")}</span>
        </label>

        {error && (
          <p style={{ color: "var(--color-danger,#d4543b)", fontSize: 12, marginBottom: 14 }}>
            {error}
          </p>
        )}

        {/* Action row */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              flex: 1,
              padding: "12px 0",
              border: "1.5px solid var(--color-border-strong)",
              borderRadius: 12,
              background: "transparent",
              fontSize: 13,
              fontWeight: 700,
              color: "var(--color-text-muted)",
            }}
          >
            {t("form.cancel")}
          </button>
          <button
            type="submit"
            disabled={!baseValid || busy}
            style={{
              flex: 2,
              padding: "12px 0",
              border: 0,
              borderRadius: 12,
              background: !baseValid || busy
                ? "var(--color-border-strong)"
                : "var(--color-primary-fill)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            {busy
              ? t("form.submitting")
              : isEdit
                ? t("form.save")
                : t("form.create")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Address skeleton ─────────────────────────────────────────────────────────
function AddressSkeleton() {
  return (
    <div
      style={{
        border: "1.5px solid var(--color-border)",
        borderRadius: 14,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <TgSkeleton height={12} width="40%" />
      <TgSkeleton height={14} width="60%" />
      <TgSkeleton height={11} width="35%" />
      <TgSkeleton height={11} width="80%" />
    </div>
  );
}

// ─── Address card ─────────────────────────────────────────────────────────────
interface CardProps {
  addr: AddressVo;
  busy: boolean;
  onSetDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
}

function AddressCard({ addr, busy, onSetDefault, onEdit, onDelete, onCopy }: CardProps) {
  const { t } = useTranslation("address");

  const cardStyle = addr.isDefault
    ? {
        background: "linear-gradient(180deg,var(--color-primary-soft),#fff)",
        border: "1.5px solid var(--color-primary-fill)",
        borderRadius: 14,
        padding: "14px 16px",
      }
    : {
        background: "#fff",
        border: "1.5px solid var(--color-border)",
        borderRadius: 14,
        padding: "14px 16px",
      };

  const displayPhone = formatRuMobileDisplay(addr.mobile);

  return (
    <div style={cardStyle}>
      {/* Tags row */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
        {addr.isDefault && (
          <span
            style={{
              padding: "2px 8px",
              background: "var(--color-primary-fill)",
              color: "#fff",
              fontFamily: "monospace",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.04em",
              borderRadius: 999,
            }}
          >
            {t("card.default")}
          </span>
        )}
      </div>

      {/* Name */}
      <strong style={{ fontSize: 13, display: "block" }}>
        {addr.recipientName || "—"}
      </strong>

      {/* Phone */}
      <div
        style={{
          fontFamily: "monospace",
          fontSize: 11,
          fontWeight: 700,
          marginTop: 3,
        }}
      >
        {displayPhone}
      </div>

      {/* Address line */}
      <div
        style={{
          fontSize: 11,
          color: "var(--color-text-muted)",
          marginTop: 6,
          lineHeight: 1.5,
        }}
      >
        {addr.fullAddress}
        {addr.postalCode ? ` · ${addr.postalCode}` : ""}
      </div>

      {/* Actions row — dashed border-top per design */}
      <div
        style={{
          display: "flex",
          gap: 2,
          marginTop: 10,
          paddingTop: 8,
          borderTop: "1px dashed var(--color-border)",
          alignItems: "center",
        }}
      >
        {/* Set default (non-default cards only) */}
        {!addr.isDefault && (
          <button
            type="button"
            disabled={busy}
            onClick={onSetDefault}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--color-primary-fill)",
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 8px",
            }}
          >
            {t("card.set_default")}
          </button>
        )}

        {/* Edit */}
        <button
          type="button"
          disabled={busy}
          onClick={onEdit}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--color-text-muted)",
            fontSize: 11,
            fontWeight: 700,
            padding: "4px 8px",
          }}
        >
          {t("card.edit")}
        </button>

        {/* Copy — only on default card per design, but shown on all for utility */}
        {addr.isDefault && (
          <button
            type="button"
            onClick={onCopy}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--color-text-muted)",
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 8px",
            }}
          >
            {t("card.copy")}
          </button>
        )}

        {/* Delete — right aligned via margin-left auto */}
        <button
          type="button"
          disabled={busy}
          onClick={onDelete}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--color-danger,#d4543b)",
            fontSize: 11,
            fontWeight: 700,
            padding: "4px 8px",
            marginLeft: "auto",
          }}
        >
          {t("card.delete")}
        </button>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export function AddressPage() {
  const { t } = useTranslation("address");
  const { t: tCommon } = useTranslation("common");
  const auth = useAuth();
  const authToken = useAuthToken();

  const { data, isLoading } = useAddresses(authToken);
  const del = useDeleteAddresses(authToken);
  const setDef = useSetDefaultAddress(authToken);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; addr: AddressVo | null }>({
    open: false,
    addr: null,
  });

  const items = data ?? [];
  const remaining = Math.max(0, MAX_ADDRESSES - items.length);
  const canAdd = remaining > 0;

  const openAdd = () => setModal({ open: true, addr: null });
  const openEdit = (addr: AddressVo) => setModal({ open: true, addr });
  const closeModal = () => setModal({ open: false, addr: null });

  const handleSetDefault = async (id: string) => {
    setBusyId(id);
    try {
      await setDef.mutateAsync(id);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("confirm.delete"))) return;
    setBusyId(id);
    try {
      await del.mutateAsync([id]);
    } finally {
      setBusyId(null);
    }
  };

  const handleCopy = (addr: AddressVo) => {
    const text = [
      addr.recipientName,
      formatRuMobileDisplay(addr.mobile),
      addr.fullAddress,
      addr.postalCode,
    ]
      .filter(Boolean)
      .join("\n");
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  // ── Auth gate ──
  if (auth.status === "booting") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div style={{ padding: "16px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
          <AddressSkeleton />
          <AddressSkeleton />
        </div>
      </TgPage>
    );
  }

  if (auth.status === "signed-out") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div style={{ padding: "16px 12px" }}>
          <TgEmpty
            icon="🔐"
            title={t("state.signin_t")}
            hint={t("state.signin_s")}
          />
        </div>
      </TgPage>
    );
  }

  return (
    <TgPage tabbar={false}>
      <TgHeader
        title={t("head.title")}
        back
        right={
          canAdd ? (
            <button
              type="button"
              onClick={openAdd}
              style={{
                background: "transparent",
                border: 0,
                fontSize: 20,
                fontWeight: 700,
                color: "var(--color-primary-fill)",
                lineHeight: 1,
                padding: "0 4px",
              }}
            >
              +
            </button>
          ) : undefined
        }
      />

      <div
        className="tg-body"
        style={{ flex: 1, padding: "12px 12px", display: "flex", flexDirection: "column", gap: 10 }}
      >
        {/* Count subtitle */}
        {!isLoading && items.length > 0 && (
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", padding: "0 4px" }}>
            {t("subtitle_count", `已添加 {count} 个 · 最多保存 {max} 个`, {
              count: items.length,
              max: MAX_ADDRESSES,
            })}
          </div>
        )}

        {/* Skeleton */}
        {isLoading && (
          <>
            <AddressSkeleton />
            <AddressSkeleton />
          </>
        )}

        {/* Empty */}
        {!isLoading && items.length === 0 && (
          <TgEmpty
            icon="📍"
            title={t("empty.title")}
            hint={t("empty.hint")}
          />
        )}

        {/* Address cards */}
        {items.map((a) => (
          <AddressCard
            key={a.id}
            addr={a}
            busy={busyId === a.id}
            onSetDefault={() => handleSetDefault(a.id)}
            onEdit={() => openEdit(a)}
            onDelete={() => handleDelete(a.id)}
            onCopy={() => handleCopy(a)}
          />
        ))}

        {/* Inline dashed "add new" card */}
        {!isLoading && canAdd && (
          <button
            type="button"
            onClick={openAdd}
            style={{
              background: "transparent",
              border: "2px dashed var(--color-border-strong)",
              borderRadius: 14,
              padding: 18,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              cursor: "pointer",
              color: "var(--color-text)",
              width: "100%",
            }}
          >
            <div style={{ fontSize: 24 }}>+</div>
            <strong style={{ fontSize: 12 }}>{t("head.add")}</strong>
            <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
              {t("remaining_count", `还可添加 {n} 个`, { n: remaining })}
            </span>
          </button>
        )}
      </div>

      {/* Form modal */}
      {modal.open && (
        <AddressFormModal
          authToken={authToken}
          initial={modal.addr}
          onClose={closeModal}
        />
      )}
    </TgPage>
  );
}
